// @refresh reset
// ─────────────────────────────────────────────────────────────────────────────
// EVERYTHING 420 — THE FEATURE TABLE AND PRICING SHEET.
//
// Same instrument Hank built for Air Duct (airduct/calculator.tsx): every blue
// number is an input, totals recompute live, nothing is stored. It is a what-if
// surface for Erez, not a document.
//
// WHAT IS DIFFERENT HERE, AND IT MATTERS: this list has PROVENANCE. Three
// sources, and they are not equally strong —
//
//   SHUKI   he asked for it, in his own words, on 19 July (meeting 83).
//           These are the only rows we can quote with confidence.
//   SPEC    it appears in the specification another provider produced for them.
//           He did NOT engage her — "I did not move forward with her" — but he
//           passed us the document and said he agrees with its first project.
//           So these are validated demand, not our invention.
//   OURS    we are proposing it. Nothing has been said about it either way.
//
// A row's source is shown in the table because a price on something the client
// never mentioned is a different conversation from a price on something he
// asked for twice.
//
// SIZING. S/M/L is effort, not value. The bands are deliberately the same
// numbers we quoted Air Duct — one honest rate across clients, the scope moves,
// never the rate.
//
// USERS. Their office is about five people (Shuki, on the call). So the user
// multiplier barely moves here, which is correct: this is a small team getting
// leverage, not a large one needing seats. 0 users means a MECHANISM with no
// screen — nobody opens the shipping-label watcher, it just runs.
//
// 🔴 in a cell = our estimate, not something they stated.
//
// Owner agent: Ruby.
// ─────────────────────────────────────────────────────────────────────────────

import { useState, useMemo } from "react";
import { N, NFONT, NMONO, R, R_CTL } from "./notion";

const F = NFONT;
const MONO = NMONO;

type Size = "S" | "M" | "L" | "?";
type Source = "SHUKI" | "SPEC" | "OURS";

type Feature = {
  id: string;
  name: string;
  note: string;
  size: Size;
  /** Who OPENS the screen. 0 = a mechanism with no screen. null = unknown. */
  users: number | null;
  source: Source;
  /** true when a figure on this row is our estimate rather than stated. */
  est: boolean;
  phase1: boolean;
};

/** Every row traces to the 19 July call, the specification we were handed, or
 *  is marked as ours. Ordered as Shuki raised them, then the rest. */
const FEATURES: Feature[] = [
  // ── Shuki's own list, in the order he said it ──
  { id: "f1", name: "Product image pipeline", source: "SHUKI", size: "L", users: 2, est: true, phase1: true,
    note: "Supplier photos in, a publishable product-page set out. Reads the product and its description, works to a written standard, one image at a time until the standard holds. This is the one he wants first and it is urgent — merchandise is on the way and already photographed." },
  { id: "f2", name: "Purchasing and purchase orders", source: "SHUKI", size: "L", users: 3, est: true, phase1: false,
    note: "See stock, see what is about to run out, forecast the coming months against average sales, build the PO and send it to the supplier. Today a person does it and it takes a week; he wants a button and a day. 100+ suppliers, 6,000+ products." },
  { id: "f3", name: "Landing page and CRO testing", source: "SHUKI", size: "M", users: 3, est: true, phase1: false,
    note: "Take a page's layout, its analytics events, heatmaps and scroll maps, plus what leading sites in the category do, and return ten recommended A/B tests against a chosen KPI." },
  { id: "f4", name: "Customer support agent", source: "SHUKI", size: "M", users: 4, est: true, phase1: false,
    note: "Order lookup by number, tag or date, against the written support SOP. Raised by him as a whole domain worth taking on, given roughly 60,000 orders a month." },
  { id: "f5", name: "Shipping-label failure watch", source: "SHUKI", size: "M", users: 0, est: false, phase1: false,
    note: "Labels intermittently fail to print — 10 to 15 in a batch — and the team believes the batch shipped. Detectable from the data or from a tracking number that never updates. His own figure: roughly $250 a day leaking." },
  { id: "f6", name: "Ask the business a question", source: "SHUKI", size: "L", users: 5, est: true, phase1: false,
    note: "The portal he described unprompted: one place every employee can use, that centralises information from all their systems so people can talk to the data. Answers cite the rows they came from." },

  // ── In the specification he was given, and agrees with ──
  { id: "f7", name: "Marketing performance agent", source: "SPEC", size: "M", users: 3, est: true, phase1: false,
    note: "Campaign and flow performance against previous period, segment and profile lookups. Their real questions look like: did moving promos to $40+ orders actually lift AOV." },
  { id: "f8", name: "Sheets to store bridge", source: "SPEC", size: "M", users: 4, est: true, phase1: false,
    note: "Read a working sheet's structure and fill its empty cells from the store — the pattern their team already runs by hand for wholesale and collection exports." },
  { id: "f9", name: "Vendor and product analysis", source: "SPEC", size: "M", users: 3, est: true, phase1: false,
    note: "Top vendors over a period, single-vendor summaries, and trending products by type. Feeds the purchasing decision above." },
  { id: "f10", name: "Bulk product operations", source: "SPEC", size: "M", users: 2, est: true, phase1: false,
    note: "Mass product creation and update, B2B upload preparation, bulk stock setting with a snapshot to roll back to. Today this is a folder of one-off scripts run by hand." },
  { id: "f11", name: "Run management and guardrails", source: "SPEC", size: "M", users: 2, est: true, phase1: false,
    note: "Every run visible with its logs, permissions per role, a spend cap per agent. Their current setup has the tables for this and they are empty — nothing is scheduled, nothing is audited." },
  { id: "f12", name: "Wholesale sheet sync", source: "SPEC", size: "S", users: 2, est: true, phase1: false,
    note: "Keep the wholesale sheet and the store in step, to the protocol already written down on their side." },

  // ── Ours. Nothing has been said about these either way. ──
  { id: "f13", name: "State shippability matrix", source: "OURS", size: "M", users: 3, est: true, phase1: false,
    note: "Their own terms carry a per-category, per-state prohibition list — THC-A barred in seven states, delta-8 in about twenty-five — plus an age gate that varies by state. No system they own has a field for it. An operational aid on rules they own, never a compliance guarantee." },
  { id: "f14", name: "Listing generation from intake", source: "OURS", size: "M", users: 2, est: true, phase1: false,
    note: "The same intake that produces the images also produces the title, description and attributes. Their storefront filters on colour, height and joint type, so attributes are worth real money to them." },
  { id: "f15", name: "Migration assist — store platform", source: "OURS", size: "?", users: null, est: true, phase1: false,
    note: "They are mid-forced-migration off Shopify after a policy change threatened half their catalogue. Erez raised on the call that this is exactly where an outsider could help. Unsized on purpose — it depends entirely on what their own team wants to keep." },
];

/** Defaults. THE RATE CARD IS SHARED — these numbers must stay identical to
 *  RATE in board/command/proposal.tsx and to Hank's airduct/calculator.tsx. One
 *  honest rate across clients: the scope moves, the rate does not.
 *
 *  Setup is ONE flat number (Erez, 2026-07-29). It used to be discovery plus the
 *  system plus a data layer plus six connectors, which is a build sheet, not a
 *  price — and it is now a single field in the proposal too, so keeping four
 *  addends here would let this sheet drift off the client-facing number one knob
 *  at a time. The client buys "the system, set up". */
const DEFAULT_KNOBS = {
  S: 1200, M: 2000, L: 4500,
  b1: 1.0, b2: 1.1, b3: 1.2, b4: 1.3,
  setup: 5000,
  retainer: 2500,
};

type Knobs = typeof DEFAULT_KNOBS;

const usd = (n: number) => "$" + Math.round(n).toLocaleString();

/** Banded and capped. Ten users do not cost five times two. */
function bandOf(users: number | null, k: Knobs): { m: number; label: string } {
  if (users == null) return { m: 0, label: "—" };
  if (users === 0) return { m: k.b1, label: "no screen" };
  if (users <= 2) return { m: k.b1, label: "1-2" };
  if (users <= 10) return { m: k.b2, label: "3-10" };
  if (users <= 30) return { m: k.b3, label: "11-30" };
  return { m: k.b4, label: "31+" };
}

function basePrice(size: Size, k: Knobs): number | null {
  if (size === "S") return k.S;
  if (size === "M") return k.M;
  if (size === "L") return k.L;
  return null;
}

const SOURCE_STYLE: Record<Source, { bg: string; fg: string; label: string }> = {
  SHUKI: { bg: N.greenBg, fg: N.green, label: "he asked" },
  SPEC: { bg: N.blueBg, fg: N.blue, label: "in the spec" },
  OURS: { bg: N.purpleBg, fg: N.purple, label: "ours" },
};

/** An editable number cell. Blue = you can change it. */
function Num({ value, onChange, width = 78, step = 100, prefix, suffix }: {
  value: number; onChange: (n: number) => void; width?: number;
  step?: number; prefix?: string; suffix?: string;
}) {
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 1 }}>
      {prefix && <span style={{ color: N.blue, fontFamily: MONO, fontSize: 12.5 }}>{prefix}</span>}
      <input
        type="number"
        value={value}
        step={step}
        onChange={(e) => {
          const n = parseFloat(e.target.value);
          onChange(Number.isFinite(n) ? n : 0);
        }}
        style={{
          width, fontFamily: MONO, fontSize: 12.5, fontWeight: 500, color: N.blue,
          background: N.blueBg, border: "1px solid transparent", borderRadius: R_CTL,
          padding: "3px 6px", textAlign: "right", outline: "none",
        }}
        onFocus={(e) => e.currentTarget.select()}
      />
      {suffix && <span style={{ color: N.ink3, fontFamily: MONO, fontSize: 11.5 }}>{suffix}</span>}
    </span>
  );
}

function Th({ children, align = "left", w }: { children: React.ReactNode; align?: "left" | "right" | "center"; w?: number }) {
  return (
    <th style={{
      textAlign: align, width: w, padding: "9px 12px", fontFamily: MONO, fontSize: 10.5,
      fontWeight: 700, letterSpacing: ".09em", textTransform: "uppercase", color: N.ink2,
      background: N.bgGray, borderBottom: `1px solid ${N.line}`, whiteSpace: "nowrap",
    }}>{children}</th>
  );
}

function Td({ children, align = "left", mono, bold, color, top }: {
  children: React.ReactNode; align?: "left" | "right" | "center";
  mono?: boolean; bold?: boolean; color?: string; top?: boolean;
}) {
  return (
    <td style={{
      textAlign: align, padding: "10px 12px", fontSize: mono ? 13 : 13.5,
      fontFamily: mono ? MONO : F, fontWeight: bold ? 700 : 500,
      color: color ?? N.ink, borderBottom: `1px solid ${N.line}`,
      verticalAlign: top ? "top" : "middle",
    }}>{children}</td>
  );
}

function KnobRow({ label, children, hint }: { label: string; children: React.ReactNode; hint?: string }) {
  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, padding: "5px 0" }}>
      <span style={{ fontSize: 12.5, color: N.ink2 }}>
        {label}
        {hint && <span style={{ color: N.ink3, fontSize: 11 }}> {hint}</span>}
      </span>
      {children}
    </div>
  );
}

function Panel({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div style={{ background: N.bg, border: `1px solid ${N.line}`, borderRadius: R, padding: "12px 14px" }}>
      <div style={{
        fontFamily: MONO, fontSize: 10.5, fontWeight: 700, letterSpacing: ".09em",
        textTransform: "uppercase", color: N.ink3, marginBottom: 6,
      }}>{title}</div>
      {children}
    </div>
  );
}

function Total({ label, value, sub, tone }: { label: string; value: string; sub: string; tone: "green" | "blue" | "ink" }) {
  const map = {
    green: { bg: N.greenBg, ink: N.green },
    blue: { bg: N.blueBg, ink: N.blue },
    ink: { bg: N.grayBg, ink: N.ink },
  }[tone];
  return (
    <div style={{ flex: 1, background: map.bg, borderRadius: R, padding: "13px 16px", minWidth: 190 }}>
      <div style={{
        fontFamily: MONO, fontSize: 10, fontWeight: 700, letterSpacing: ".1em",
        textTransform: "uppercase", color: map.ink, opacity: 0.75,
      }}>{label}</div>
      <div style={{ fontFamily: F, fontSize: 30, fontWeight: 600, color: map.ink, letterSpacing: "-.02em", lineHeight: 1.15, marginTop: 2 }}>{value}</div>
      <div style={{ fontSize: 11.5, color: N.ink2, marginTop: 2 }}>{sub}</div>
    </div>
  );
}

export function Everything420PricingSheet() {
  const [k, setK] = useState<Knobs>(DEFAULT_KNOBS);
  const [rows, setRows] = useState<Feature[]>(FEATURES);

  const set = (key: keyof Knobs) => (n: number) => setK((p) => ({ ...p, [key]: n }));
  const patch = (id: string, d: Partial<Feature>) =>
    setRows((p) => p.map((r) => (r.id === id ? { ...r, ...d } : r)));

  const calc = useMemo(() => {
    const setup = k.setup;
    const priced = rows.map((r) => {
      const base = basePrice(r.size, k);
      const band = bandOf(r.users, k);
      const price = base == null || band.m === 0 ? null : base * band.m;
      return { ...r, base, band, price };
    });
    const featuresAll = priced.reduce((s, r) => s + (r.price ?? 0), 0);
    const phase1Features = priced.reduce((s, r) => s + (r.phase1 ? r.price ?? 0 : 0), 0);
    const quotable = priced.filter((r) => r.price != null).length;
    const inPhase1 = priced.filter((r) => r.phase1 && r.price != null).length;
    const askedFor = priced.filter((r) => r.source === "SHUKI").length;
    return {
      setup, priced, featuresAll, phase1Features, quotable, inPhase1, askedFor,
      phase1: setup + phase1Features,
      whole: setup + featuresAll,
    };
  }, [k, rows]);

  const sizeBtn = (r: Feature, s: Size) => {
    const on = r.size === s;
    return (
      <button
        key={s}
        onClick={() => patch(r.id, { size: s })}
        style={{
          width: 26, height: 24, borderRadius: R_CTL, cursor: "pointer",
          fontFamily: MONO, fontSize: 11.5, fontWeight: 600,
          border: `1px solid ${on ? "transparent" : N.lineSoft}`,
          background: on ? (s === "?" ? N.red : N.ink) : N.bg,
          color: on ? "#FFF" : N.ink3,
        }}
      >{s}</button>
    );
  };

  return (
    // nowheel/nodrag/nopan + stopPropagation + pointerEvents is MANDATORY on an
    // interactive board node, or React Flow swallows every click before it
    // reaches an input and the sheet renders perfectly while doing nothing.
    <div
      className="nowheel nodrag nopan"
      data-zoom
      onPointerDown={(e) => e.stopPropagation()}
      style={{
        pointerEvents: "auto",
        width: 1680, background: N.bg, border: `1px solid ${N.line}`,
        borderRadius: R, padding: 26, fontFamily: F, boxSizing: "border-box",
      }}
    >
      <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", marginBottom: 14 }}>
        <div>
          <div style={{ fontSize: 26, fontWeight: 700, color: N.ink, letterSpacing: "-.02em" }}>
            The feature table
          </div>
          <div style={{ fontSize: 12.5, color: N.ink2, marginTop: 3 }}>
            Every <span style={{ color: N.blue, fontWeight: 700 }}>blue</span> number is editable. Totals recompute live. Nothing is saved.
          </div>
        </div>
        <button
          onClick={() => { setK(DEFAULT_KNOBS); setRows(FEATURES); }}
          style={{
            fontFamily: MONO, fontSize: 11, fontWeight: 700, letterSpacing: ".06em",
            color: N.ink2, background: N.bg, border: `1px solid ${N.lineStrong}`,
            borderRadius: R_CTL, padding: "6px 12px", cursor: "pointer",
          }}
        >Reset</button>
      </div>

      <div style={{ display: "flex", gap: 12, marginBottom: 16 }}>
        <Total tone="green" label="Phase 1 — setup + the images" value={usd(calc.phase1)}
          sub={`${calc.inPhase1} feature${calc.inPhase1 === 1 ? "" : "s"} ticked in · images first, and urgent`} />
        <Total tone="ink" label="Setup — the system, set up" value={usd(calc.setup)}
          sub="one flat number, same on every client" />
        <Total tone="blue" label="Everything on this table" value={usd(calc.whole)}
          sub={`setup + all ${calc.quotable} quotable features`} />
        <Total tone="ink" label="Retainer" value={usd(k.retainer) + "/mo"}
          sub="runs it, keeps the layer current" />
      </div>

      <div style={{ display: "flex", gap: 16, alignItems: "flex-start" }}>
        <div style={{ width: 300, flexShrink: 0, display: "flex", flexDirection: "column", gap: 10 }}>
          <Panel title="Feature base price">
            <KnobRow label="Small"><Num value={k.S} onChange={set("S")} step={250} prefix="$" /></KnobRow>
            <KnobRow label="Medium"><Num value={k.M} onChange={set("M")} step={250} prefix="$" /></KnobRow>
            <KnobRow label="Large"><Num value={k.L} onChange={set("L")} step={250} prefix="$" /></KnobRow>
            <div style={{ fontSize: 11, color: N.ink3, lineHeight: 1.45, marginTop: 5, borderTop: `1px solid ${N.line}`, paddingTop: 6 }}>
              Deliberately the same rate we quoted Air Duct. One honest rate across clients — the scope moves, the rate does not.
            </div>
          </Panel>

          <Panel title="User bands — capped multiplier">
            <KnobRow label="1-2 users"><Num value={k.b1} onChange={set("b1")} step={0.05} width={62} suffix="x" /></KnobRow>
            <KnobRow label="3-10 users"><Num value={k.b2} onChange={set("b2")} step={0.05} width={62} suffix="x" /></KnobRow>
            <KnobRow label="11-30 users"><Num value={k.b3} onChange={set("b3")} step={0.05} width={62} suffix="x" /></KnobRow>
            <KnobRow label="31+ users"><Num value={k.b4} onChange={set("b4")} step={0.05} width={62} suffix="x" /></KnobRow>
            <div style={{ fontSize: 11, color: N.ink3, lineHeight: 1.45, marginTop: 5, borderTop: `1px solid ${N.line}`, paddingTop: 6 }}>
              Their office is about five people, so this barely moves. That is correct — it is a small team buying leverage, not seats.
            </div>
          </Panel>

          <Panel title="Setup">
            <KnobRow label="Setup, all in"><Num value={k.setup} onChange={set("setup")} step={500} prefix="$" /></KnobRow>
            <div style={{ fontSize: 11, color: N.ink3, lineHeight: 1.45, marginTop: 5, borderTop: `1px solid ${N.line}`, paddingTop: 6 }}>
              One flat number, identical on every client, and identical to the figure on the proposal. It used to be four lines — discovery, the system, a data layer, connectors — which is a build sheet, not a price.
            </div>
          </Panel>

          <Panel title="After">
            <KnobRow label="Retainer / month"><Num value={k.retainer} onChange={set("retainer")} step={250} prefix="$" /></KnobRow>
          </Panel>
        </div>

        <div style={{ flex: 1, minWidth: 0, border: `1px solid ${N.line}`, borderRadius: R, overflow: "hidden" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <Th w={34}>P1</Th>
                <Th>Feature</Th>
                <Th w={96}>Source</Th>
                <Th align="center" w={116}>Size</Th>
                <Th align="right" w={70}>Users</Th>
                <Th align="right" w={92}>Band</Th>
                <Th align="right" w={104}>Price</Th>
              </tr>
            </thead>
            <tbody>
              {calc.priced.map((r) => {
                const src = SOURCE_STYLE[r.source];
                return (
                  <tr key={r.id} style={{ background: r.phase1 ? N.greenBg : N.bg }}>
                    <Td align="center" top>
                      <input
                        type="checkbox"
                        checked={r.phase1}
                        onChange={(e) => patch(r.id, { phase1: e.target.checked })}
                        style={{ accentColor: N.green, cursor: "pointer", marginTop: 3 }}
                      />
                    </Td>
                    <Td top>
                      <div style={{ fontWeight: 600, color: N.ink }}>{r.name}</div>
                      <div style={{ fontSize: 12, color: N.ink2, lineHeight: 1.5, marginTop: 3, maxWidth: 620 }}>{r.note}</div>
                    </Td>
                    <Td top>
                      <span style={{
                        fontSize: 11.5, fontWeight: 500, padding: "2px 7px", borderRadius: R,
                        background: src.bg, color: src.fg, whiteSpace: "nowrap",
                      }}>{src.label}</span>
                    </Td>
                    <Td align="center" top>
                      <div style={{ display: "inline-flex", gap: 4 }}>
                        {(["S", "M", "L", "?"] as Size[]).map((s) => sizeBtn(r, s))}
                      </div>
                    </Td>
                    <Td align="right" mono top color={r.users == null ? N.red : N.ink2}>
                      {r.users == null ? "?" : r.users === 0 ? "—" : r.users}
                      {r.est && r.users != null && <span style={{ color: N.red }}> 🔴</span>}
                    </Td>
                    <Td align="right" mono top color={N.ink3}>{r.band.label}</Td>
                    <Td align="right" mono bold top color={r.price == null ? N.red : N.ink}>
                      {r.price == null ? "not sized" : usd(r.price)}
                    </Td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          <div style={{
            display: "flex", justifyContent: "space-between", gap: 16,
            padding: "12px 14px", background: N.bgGray, borderTop: `1px solid ${N.line}`,
            fontSize: 12, color: N.ink2, lineHeight: 1.5,
          }}>
            <span>
              <b style={{ color: N.green }}>{calc.askedFor} of {rows.length}</b> rows are things Shuki asked for by name.
              The rest are from the specification he was given, or ours — priced the same, but a very different conversation.
            </span>
            <span style={{ whiteSpace: "nowrap" }}>🔴 = our estimate, not stated</span>
          </div>
        </div>
      </div>
    </div>
  );
}
